import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Box, Button, Stack, Typography } from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useLazyUploadScannedExpenseDocumentQuery } from '../../../redux/api/independentApi'
import { ExpenseAnalysisResponse } from '../../../sdk'

type OnDropHandler = DropzoneOptions['onDrop']

interface Props {
  analysisSuccessCallback: (
    image: Blob,
    analysisResults: ExpenseAnalysisResponse
  ) => Promise<void>
}

const UploadPreview: FC<Props> = ({ analysisSuccessCallback }) => {
  const file = useRef<File | null>(null)
  const [imgObjUrl, setImgObjUrl] = useState<string>('')
  const updateObjUrl = useCallback(() => {
    setImgObjUrl(file.current ? URL.createObjectURL(file.current) : '')
  }, [])

  const onDrop: OnDropHandler = useCallback(
    (acceptedFiles: File[]) => {
      file.current = acceptedFiles[0] || null
      updateObjUrl()
    },
    [updateObjUrl]
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  const handleClearImage = () => {
    file.current = null
    updateObjUrl()
  }

  useEffect(() => {
    return () => {
      if (imgObjUrl) {
        URL.revokeObjectURL(imgObjUrl)
      }
    }
  }, [imgObjUrl])

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [uploadScannedDocument, { isLoading }] =
    useLazyUploadScannedExpenseDocumentQuery()
  const handleUpload = async () => {
    const results = await uploadScannedDocument({
      authorization: accessToken,
      file: file.current!,
      shopId
    })
    if ('data' in results) {
      analysisSuccessCallback(file.current!, results.data!)
    }
  }

  return (
    <Stack spacing={1}>
      <Stack flexDirection="row" justifyContent="space-between">
        <Typography variant="h3">
          Document Analysis{imgObjUrl && ' Review'}
        </Typography>
        {imgObjUrl && (
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleClearImage}
              disabled={isLoading}
              color="error">
              Delete
            </Button>
            <Button
              onClick={handleUpload}
              disabled={isLoading}
              variant="outlined">
              {isLoading ? 'Sending...' : 'Upload'}
            </Button>
          </Stack>
        )}
      </Stack>
      {imgObjUrl ? (
        <Box>
          <Typography>
            To proceed with the analysis, select the Upload button or select the
            Delete button to choose another image.
          </Typography>
          <img
            alt="Preview of selected file"
            src={imgObjUrl}
            style={{ objectFit: 'scale-down', width: 500 }}
          />
        </Box>
      ) : (
        <>
          <Typography>
            Simply upload an image file of an invoice and our system can analyze
            the text and automatically fill in the relevant information.
          </Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: 1,
              backgroundColor: 'primary.light',
              padding: 1,
              minHeight: '10rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <input {...getInputProps()} accept="image/png, image/jpeg" />
            <UploadFileIcon fontSize="large" />
            {isDragActive ? (
              <Typography>
                Release the mouse button to preview to image file before
                uploading.
              </Typography>
            ) : (
              <>
                <Typography align="center">
                  Drag and drop the file here or click inside this box to select
                  a file.
                </Typography>
                <Typography align="center">
                  Only PNG and JPEG image files accepted.*
                </Typography>
              </>
            )}
          </Box>
        </>
      )}
    </Stack>
  )
}

export default UploadPreview
